import React from "react";
import { observer } from "mobx-react";
import { TenantDocumentUpdateStore } from "../../store";
import { TenantDocumentUpdateForm } from "./TenantDocumentUpdateForm";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { OutdatedAfterSaveNotice } from "../../../../common/controls/forms/OutdatedAfterSaveNotice";
import { ChunkingStrategy } from "../../../../api/graphql";

export type TenantDocumentUpdateProps = {
  tenantDocumentUpdateStore: TenantDocumentUpdateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const TenantDocumentUpdate: React.FC<TenantDocumentUpdateProps> =
  observer(
    ({ tenantDocumentUpdateStore, showToolbox = true, showSaveFooter }) => {
      return (
        <>
          <SavingOverlay open={tenantDocumentUpdateStore.isSaving} />
          <OutdatedAfterSaveNotice
            open={tenantDocumentUpdateStore.savedButOutdated}
            onReload={() => tenantDocumentUpdateStore.reloadTenantDocument()}
            reloadDisabled={tenantDocumentUpdateStore.isProcessing}
          />
          <TenantDocumentUpdateForm
            tenantDocumentUpdateStore={tenantDocumentUpdateStore}
            nameError={tenantDocumentUpdateStore.errors.name?.message}
            onNameChange={(value: string) => {
              tenantDocumentUpdateStore.setNameValue(value);
            }}
            name={tenantDocumentUpdateStore.entityValues.name}
            descriptionError={
              tenantDocumentUpdateStore.errors.description?.message
            }
            onDescriptionChange={(value: string) => {
              tenantDocumentUpdateStore.setDescriptionValue(value);
            }}
            description={tenantDocumentUpdateStore.entityValues.description}
            fileOnRestoreFile={(id: string) =>
              tenantDocumentUpdateStore.fileRestoreFile(id)
            }
            fileOnLoadFile={(file: any) =>
              tenantDocumentUpdateStore.fileUploadFile(file)
            }
            fileOnCommitFile={(changes) =>
              tenantDocumentUpdateStore.fileCommitFile(changes)
            }
            fileFiles={tenantDocumentUpdateStore.entityValues.__file_files}
            fileError={tenantDocumentUpdateStore.errors.file?.message}
            processingErrorError={
              tenantDocumentUpdateStore.errors.processingError?.message
            }
            onProcessingErrorChange={(value: string) => {
              tenantDocumentUpdateStore.setProcessingErrorValue(value);
            }}
            processingError={
              tenantDocumentUpdateStore.entityValues.processingError
            }
            contentTypeError={
              tenantDocumentUpdateStore.errors.contentType?.message
            }
            onContentTypeChange={(value: string) => {
              tenantDocumentUpdateStore.setContentTypeValue(value);
            }}
            contentType={tenantDocumentUpdateStore.entityValues.contentType}
            chunkingStrategyError={
              tenantDocumentUpdateStore.errors.chunkingStrategy?.message
            }
            chunkingStrategyOptions={tenantDocumentUpdateStore.getChunkingStrategyOptions()}
            onChunkingStrategyChange={(value: ChunkingStrategy) => {
              tenantDocumentUpdateStore.setChunkingStrategyValue(value);
            }}
            chunkingStrategy={
              tenantDocumentUpdateStore.entityValues.chunkingStrategy
            }
            chunkingSeparatorRegexError={
              tenantDocumentUpdateStore.errors.chunkingSeparatorRegex?.message
            }
            onChunkingSeparatorRegexChange={(value: string) => {
              tenantDocumentUpdateStore.setChunkingSeparatorRegexValue(value);
            }}
            chunkingSeparatorRegex={
              tenantDocumentUpdateStore.entityValues.chunkingSeparatorRegex
            }
            chunkingMaxTokensError={
              tenantDocumentUpdateStore.errors.chunkingMaxTokens?.message
            }
            onChunkingMaxTokensChange={(value: number | undefined) => {
              tenantDocumentUpdateStore.setChunkingMaxTokensValue(value);
            }}
            chunkingMaxTokens={
              tenantDocumentUpdateStore.entityValues.chunkingMaxTokens
            }
            chunkingOverlapTokensError={
              tenantDocumentUpdateStore.errors.chunkingOverlapTokens?.message
            }
            onChunkingOverlapTokensChange={(value: number | undefined) => {
              tenantDocumentUpdateStore.setChunkingOverlapTokensValue(value);
            }}
            chunkingOverlapTokens={
              tenantDocumentUpdateStore.entityValues.chunkingOverlapTokens
            }
            dummyCRUDRestoredObservable={
              tenantDocumentUpdateStore.dummyCRUDRestoredObservable
            }
            showToolbox={showToolbox}
            showSaveFooter={showSaveFooter}
            saving={tenantDocumentUpdateStore.saveDisabled}
            onSave={() => tenantDocumentUpdateStore.saveTenantDocument()}
            onCancel={() => tenantDocumentUpdateStore.cancelTenantDocument()}
          />
          <ErrorDialog
            open={tenantDocumentUpdateStore.errorDialogOpened}
            title={tenantDocumentUpdateStore.errorTitle}
            description={tenantDocumentUpdateStore.errorDescription}
            onClose={() => tenantDocumentUpdateStore.closeErrorDialog()}
          />
          <ConfirmCancelDialog
            open={tenantDocumentUpdateStore.confirmDialogOpened}
            title={tenantDocumentUpdateStore.confirmDialogTitle}
            description={tenantDocumentUpdateStore.confirmDialogDescription}
            onConfirm={() => tenantDocumentUpdateStore.executeConfirmedAction()}
            onCancel={() => tenantDocumentUpdateStore.closeConfirmDialog()}
          />
        </>
      );
    }
  );
